"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import {
  fetchRandomArticle,
  fetchArticleByPageId,
  WikiArticle,
  StoredArticleSummary,
  MILESTONES,
} from "@/lib/wikipedia";
import { generateQuiz, Quiz } from "@/lib/quizGenerator";
import { useArticleHistory } from "@/hooks/useArticleHistory";
import { useOCAccount } from "@/hooks/useOCAccount";
import { OCAnalytics } from "@opencampus/ochub-utils";
import { ProgressBar } from "@/components/ProgressBar";
import { MilestoneDialog } from "@/components/MilestoneDialog";
import { ArticleCard } from "@/components/ArticleCard";
import { HistoryList } from "@/components/HistoryList";
import { QuizView } from "@/components/QuizView";
import {
  MagnifyingGlassIcon,
  ClockIcon,
  TallyIcon,
  QuizIcon,
  OpenCampusLogoMark,
  OpenCampusLogo,
} from "@/components/Icons";

type TabView = "read" | "history" | "quiz";

/**
 * Format seconds into human-readable time string
 */
function formatReadingTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

const QUIZ_TIME_LIMIT = 120; // 2 minutes in seconds

export default function HomePage() {
  const [article, setArticle] = useState<WikiArticle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [celebrateMilestone, setCelebrateMilestone] = useState<
    (typeof MILESTONES)[number] | null
  >(null);
  const [activeTab, setActiveTab] = useState<TabView>("read");
  const [isFromHistory, setIsFromHistory] = useState(false); // Track if current article was loaded from history
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [quizTimeRemaining, setQuizTimeRemaining] = useState(QUIZ_TIME_LIMIT);
  const [isQuizTimerActive, setIsQuizTimerActive] = useState(false);
  const [quizArticleTitle, setQuizArticleTitle] = useState<
    string | undefined
  >();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabView | null>(null);
  const [quizAnsweredCount, setQuizAnsweredCount] = useState(0);

  const {
    history,
    isLoaded,
    lastArticle,
    startReading,
    markAsRead,
    newMilestone,
    clearNewMilestone,
    getTotalReadingTime,
  } = useArticleHistory();

  const {
    isLoggedIn,
    ocId,
    displayName,
    isInitialized: isAccountInitialized,
  } = useOCAccount();

  // Initialize analytics on mount
  const analyticsInitialized = useRef(false);
  useEffect(() => {
    if (!analyticsInitialized.current) {
      OCAnalytics.initialize("RandomWiki", { containerId: "G-VNN1SC4GEG" });
      analyticsInitialized.current = true;
    }
  }, []);

  // Load last article on mount (resume reading)
  useEffect(() => {
    if (isLoaded && lastArticle && !article) {
      setArticle(lastArticle);
      startReading(lastArticle);
    }
  }, [isLoaded, lastArticle, article, startReading]);

  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isQuizTimerActive && quizTimeRemaining > 0) {
      interval = setInterval(() => {
        setQuizTimeRemaining((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isQuizTimerActive, quizTimeRemaining]);

  // Auto-generate quiz when entering quiz tab
  useEffect(() => {
    if (
      activeTab === "quiz" &&
      !quiz &&
      !isGeneratingQuiz &&
      history.articles.length > 0
    ) {
      // Pick a random article from history
      const randomIndex = Math.floor(Math.random() * history.articles.length);
      const randomArticle = history.articles[randomIndex];

      setIsGeneratingQuiz(true);
      setQuizArticleTitle(randomArticle.title);

      // Fetch full article and generate quiz
      fetchArticleByPageId(randomArticle.pageId)
        .then((fullArticle) => {
          const newQuiz = generateQuiz(
            fullArticle.title,
            fullArticle.extract,
            5,
          );
          setQuiz(newQuiz);
          setQuizTimeRemaining(QUIZ_TIME_LIMIT);
          setIsQuizTimerActive(true);

          // Track quiz start event
          try {
            OCAnalytics.getInstance().trackEvent("start_quiz", {
              ocid: ocId,
              current_stat: history.totalRead,
            });
          } catch (e) {
            // Analytics not initialized yet, ignore
          }
        })
        .catch((err) => {
          console.error("Failed to generate quiz:", err);
          setError("Failed to generate quiz. Please try again!");
        })
        .finally(() => {
          setIsGeneratingQuiz(false);
        });
    }
  }, [
    activeTab,
    quiz,
    isGeneratingQuiz,
    history.articles,
    ocId,
    history.totalRead,
  ]);

  // Handle tab switching with exit confirmation
  const handleTabSwitch = useCallback(
    (newTab: TabView) => {
      if (activeTab === "quiz" && quiz && isQuizTimerActive) {
        // Show confirmation dialog
        setPendingTab(newTab);
        setShowExitConfirm(true);
      } else {
        setActiveTab(newTab);
      }
    },
    [activeTab, quiz, isQuizTimerActive],
  );

  const confirmExitQuiz = useCallback(() => {
    // Track leave_quiz event before clearing state
    try {
      OCAnalytics.getInstance().trackEvent("leave_quiz", {
        ocid: ocId,
        quiz_answered: quizAnsweredCount,
      });
    } catch (e) {
      // Analytics not initialized yet, ignore
    }

    // Clear quiz state
    setQuiz(null);
    setIsQuizTimerActive(false);
    setQuizTimeRemaining(QUIZ_TIME_LIMIT);
    setQuizArticleTitle(undefined);
    setQuizAnsweredCount(0);
    // Switch to pending tab
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setShowExitConfirm(false);
  }, [pendingTab, ocId, quizAnsweredCount]);

  const cancelExitQuiz = useCallback(() => {
    setPendingTab(null);
    setShowExitConfirm(false);
  }, []);

  const loadNewArticle = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFromHistory(false); // New articles are not from history
    setQuiz(null); // Clear quiz when loading new article

    // Track analytics event
    try {
      OCAnalytics.getInstance().trackEvent("get_new_wiki_page", {
        current_stat: history.totalRead,
        ocid: ocId,
      });
    } catch (e) {
      // Analytics not initialized yet, ignore
    }

    try {
      const newArticle = await fetchRandomArticle();
      setArticle(newArticle);
      startReading(newArticle); // Start tracking reading time
    } catch (err) {
      setError("Failed to fetch article. Please try again!");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [startReading, history.totalRead, ocId]);

  const handleDone = useCallback(async () => {
    if (article && !isFromHistory) {
      markAsRead(); // Mark current article as read with time spent (only for new articles)
    }
    setIsFromHistory(false);
    await loadNewArticle();
  }, [article, isFromHistory, markAsRead, loadNewArticle]);

  const handleSelectFromHistory = useCallback(
    async (storedArticle: StoredArticleSummary) => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch full article content by page ID
        const fullArticle = await fetchArticleByPageId(storedArticle.pageId);
        setArticle(fullArticle);
        setIsFromHistory(true); // Mark as loaded from history (won't count towards stats)
        setActiveTab("read"); // Switch to read view
        setQuiz(null); // Clear any existing quiz
      } catch (err) {
        setError("Failed to load article from history. Please try again!");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const handleGenerateQuiz = useCallback(() => {
    if (history.articles.length === 0) return;

    // Pick a random article from history
    const randomIndex = Math.floor(Math.random() * history.articles.length);
    const randomArticle = history.articles[randomIndex];

    setIsGeneratingQuiz(true);
    setQuizArticleTitle(randomArticle.title);

    // Fetch full article and generate quiz
    fetchArticleByPageId(randomArticle.pageId)
      .then((fullArticle) => {
        const newQuiz = generateQuiz(fullArticle.title, fullArticle.extract, 5);
        setQuiz(newQuiz);
        setQuizTimeRemaining(QUIZ_TIME_LIMIT);
        setIsQuizTimerActive(true);
      })
      .catch((err) => {
        console.error("Failed to generate quiz:", err);
        setError("Failed to generate quiz. Please try again!");
      })
      .finally(() => {
        setIsGeneratingQuiz(false);
      });
  }, [history.articles]);

  const handleMilestoneClick = useCallback(
    (milestone: (typeof MILESTONES)[number]) => {
      setCelebrateMilestone(milestone);
    },
    [],
  );

  // Show new milestone dialog
  const displayMilestone = newMilestone || celebrateMilestone;

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#141beb] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <main className="min-h-screen flex flex-col">
      {/* Header with Progress Bar - Full width on all screens */}
      <header className="sticky top-0 z-40 bg-[#f6fbff]/95 backdrop-blur-md shadow-sm w-full">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <OpenCampusLogoMark size={28} />
            <h1 className="text-lg sm:text-xl font-bold gradient-text">
              RandomWiki
            </h1>
          </div>

          {/* Tab Navigation - Center */}
          <div className="flex items-center bg-gray-100 rounded-full p-1">
            <button
              onClick={() => handleTabSwitch("read")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === "read"
                  ? "bg-white text-[#141beb] shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <MagnifyingGlassIcon size={16} /> Read
            </button>
            <button
              onClick={() => handleTabSwitch("quiz")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === "quiz"
                  ? "bg-white text-[#141beb] shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <QuizIcon size={16} /> Quiz
            </button>
            <button
              onClick={() => handleTabSwitch("history")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === "history"
                  ? "bg-white text-[#141beb] shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <ClockIcon size={16} /> History
            </button>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-[#141beb]/10 to-[#02eec4]/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
            <TallyIcon size={20} className="text-[#141beb]" />
            <span className="font-bold text-[#141beb] text-sm sm:text-base">
              {history.totalRead}
            </span>
            <span className="text-xs sm:text-sm text-gray-600 hidden xs:inline">
              articles
            </span>
          </div>
        </div>
        {/* Progress Section with Welcome Box - Fixed in header */}
        <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 pb-3 sm:pb-4">
          <h2 className="text-xs sm:text-sm font-semibold text-gray-600 mb-2 text-center">
            Your Learning Journey
          </h2>
          <div className="flex items-stretch gap-3">
            {/* User Welcome Box - Wider container */}
            <div
              className="flex-shrink-0 w-[120px] h-20 sm:w-36 sm:h-24 rounded-xl flex flex-col items-center justify-center text-center p-2"
              style={{
                background: isLoggedIn
                  ? "linear-gradient(135deg, #141beb 0%, #02eec4 100%)"
                  : "linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {isAccountInitialized ? (
                <>
                  <span className="text-white/80 text-[10px] sm:text-xs font-medium">
                    Welcome,
                  </span>
                  <span className="text-white font-bold text-[10px] sm:text-xs leading-tight mt-0.5 break-words max-w-full px-1">
                    {displayName.length > 16
                      ? displayName.slice(0, 14) + "..."
                      : displayName}
                  </span>
                  <span className="text-lg mt-0.5">
                    {isLoggedIn ? "🎓" : "🧠"}
                  </span>
                </>
              ) : (
                <span className="text-white/70 text-xs">Loading...</span>
              )}
            </div>
            {/* Progress Bar - Takes remaining space */}
            <div className="flex-1 min-w-0">
              <ProgressBar
                totalRead={history.totalRead}
                milestonesAchieved={history.milestonesAchieved}
                onMilestoneClick={handleMilestoneClick}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area - Responsive width */}
      <div className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-20 sm:pb-24 flex flex-col">
        {activeTab === "read" ? (
          <>
            {/* CTA Button - I'm Done */}
            {article && (
              <section className="mb-4 sm:mb-6">
                <button
                  onClick={handleDone}
                  disabled={isLoading}
                  className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-[#02eec4] to-[#141beb] text-white font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-xl sm:text-2xl">👍</span>
                  <span>
                    {isFromHistory
                      ? "Back to new articles!"
                      : "I'm done, give me next!"}
                  </span>
                </button>
              </section>
            )}

            {/* History indicator */}
            {isFromHistory && article && (
              <div className="mb-3 text-center text-sm text-gray-500 bg-amber-50 border border-amber-200 rounded-lg py-2 px-3">
                📜 Viewing from history (won&apos;t count towards stats)
              </div>
            )}

            {/* Article Card - Expands to fill available space */}
            <section className="mb-4 sm:mb-6 flex-1 min-h-0">
              <ArticleCard article={article} isLoading={isLoading} />
            </section>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 sm:px-4 py-2 sm:py-3 rounded-xl mb-4 sm:mb-6 text-center text-sm sm:text-base">
                {error}
              </div>
            )}

            {/* Get New Article Button */}
            <section className="flex-shrink-0">
              <button
                onClick={article ? loadNewArticle : loadNewArticle}
                disabled={isLoading}
                className={`w-full py-3 sm:py-4 px-4 sm:px-6 font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed ${
                  article
                    ? "bg-[#f6fbff] text-[#141beb] border-2 border-[#141beb] hover:bg-[#e8f4fd]"
                    : "bg-gradient-to-r from-[#141beb] to-[#02eec4] text-white"
                }`}
              >
                <span className="text-xl sm:text-2xl">✨</span>
                <span>
                  {article ? "Skip this one" : "Give me something new!"}
                </span>
              </button>
            </section>

            {/* Fun stats */}
            {history.totalRead > 0 && (
              <section className="mt-6 sm:mt-8 text-center text-gray-500 text-xs sm:text-sm space-y-1">
                <p>
                  You&apos;ve explored{" "}
                  {history.articles.length > 0 ? (
                    <span className="font-medium text-[#141beb]">
                      {
                        [
                          ...new Set(
                            history.articles.flatMap((a) => a.categories),
                          ),
                        ].length
                      }
                    </span>
                  ) : (
                    "0"
                  )}{" "}
                  different categories!
                </p>
                {getTotalReadingTime() > 0 && (
                  <p>
                    Total reading time:{" "}
                    <span className="font-medium text-[#02eec4]">
                      {formatReadingTime(getTotalReadingTime())}
                    </span>
                  </p>
                )}
              </section>
            )}
          </>
        ) : activeTab === "quiz" ? (
          /* Quiz View */
          <QuizView
            quiz={quiz}
            onGenerateQuiz={handleGenerateQuiz}
            isGenerating={isGeneratingQuiz}
            hasArticle={!!article}
            hasHistory={history.articles.length > 0}
            timeRemaining={quizTimeRemaining}
            isTimerActive={isQuizTimerActive}
            quizArticleTitle={quizArticleTitle}
            onAnswerCountChange={setQuizAnsweredCount}
          />
        ) : (
          /* History View */
          <>
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                Reading History
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {history.articles.length} article
                {history.articles.length !== 1 ? "s" : ""} read
              </p>
            </div>
            <HistoryList
              articles={history.articles}
              onSelectArticle={handleSelectFromHistory}
            />
          </>
        )}
      </div>

      {/* Exit Quiz Confirmation Dialog */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Exit Quiz?</h3>
            <p className="text-gray-600 mb-6">
              Your quiz progress will be lost. Are you sure you want to exit?
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelExitQuiz}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-all"
              >
                No, Stay
              </button>
              <button
                onClick={confirmExitQuiz}
                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all"
              >
                Yes, Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Milestone Dialog */}
      <MilestoneDialog
        milestone={displayMilestone}
        onClose={() => {
          if (newMilestone) clearNewMilestone();
          if (celebrateMilestone) setCelebrateMilestone(null);
        }}
      />

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#f6fbff]/95 backdrop-blur-md border-t border-gray-200 py-2 sm:py-3 z-30">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500">
          <span>Powered by</span>
          <OpenCampusLogo size={100} className="text-[#141beb]" />
          <span>• Articles from Wikipedia</span>
        </div>
      </footer>
    </main>
  );
}
